import { Component, OnInit, Input, OnChanges } from '@angular/core';
import momentRange from 'moment-range';
import moment from 'moment-timezone';

import { AnalyticsService } from 'app/analytics/analytics.service';
import { ChartColorScheme } from 'app/shared/model/charts/chart-color-scheme.model';
import { ChartDataIntervals } from 'app/shared/model/charts/chart-data-intervals.enum';
import { ChartSettings } from 'app/shared/model/charts/chart-settings.model';
import { ChartThumbnailDataSettings } from 'app/shared/model/charts/chart-thumbnail-data-settings.model';
import { ChartUtilsService } from 'app/shared/service/chart-utils.service';

import { FrequencyChartDataService } from '../frequency-chart-data.service';

// The require()-based block is for browsers, the import-based is for tests execution
const { extendMoment } = typeof require === 'function' ? require('moment-range') : momentRange;

const { range } = extendMoment(moment as any);

@Component({
  selector: 'cc-frequency-chart-thumbnail',
  templateUrl: './frequency-chart-thumbnail.component.html',
  styleUrls: ['./frequency-chart-thumbnail.component.scss'],
})
export class FrequencyChartThumbnailComponent implements OnInit, OnChanges {
  @Input() chart: ChartSettings;

  barChartData: Record<string, unknown>[] = [];
  cumulativeBarChartData: Record<string, unknown>[] = [];

  colorScheme = ChartColorScheme;

  loading = true;
  chartType: string;

  xLabel = '';

  constructor(
    private readonly frequencyChartDataService: FrequencyChartDataService,
    private readonly analyticsService: AnalyticsService,
    private readonly chartUtilsService: ChartUtilsService,
  ) {}

  async ngOnInit() {
    if (!this.chart.templateId) {
      this.loading = false;
      return;
    }

    this.loading = true;
    await this.loadData();
    this.loading = false;
  }

  async ngOnChanges() {
    await this.loadData();
  }

  private async loadData() {
    const forms = await this.analyticsService
      .listTemplateForms(this.chart.templateId, this.chartUtilsService.getURLParams(this.chart))
      .toPromise();

    const updatedChart = { ...this.chart };

    let dateRange = this.chartUtilsService.getRange(this.chart, forms);
    const dateUnit = this.getInterval(this.chart, dateRange);

    this.xLabel = ChartThumbnailDataSettings[this.chart.timeframe].xAxisLabel;

    if (dateUnit !== this.chart.dateUnit) {
      updatedChart.dateUnit = dateUnit;
      dateRange = this.chartUtilsService.getRange(updatedChart, forms);
    }
    const data = await this.frequencyChartDataService.parseForms(updatedChart, forms, dateRange);
    this.barChartData = data.barChartData;
    this.cumulativeBarChartData = data.cumulativeBarChartData;
    this.chartType = this.chart.showAs;
  }

  private getInterval(chart: ChartSettings, dateRange: momentRange.DateRange) {
    const month = range(moment(), moment().add(1, 'month'));
    const sixMonths = range(moment(), moment().add(6, 'month'));
    const thirtyMonths = range(moment(), moment().add(30, 'month'));

    if (dateRange >= thirtyMonths) {
      return ChartDataIntervals.Quarter;
    }

    if (dateRange >= sixMonths) {
      return ChartDataIntervals.Month;
    }

    if (dateRange >= month) {
      return [ChartDataIntervals.Day, ChartDataIntervals.Week].includes(chart.dateUnit)
        ? ChartDataIntervals.Week
        : ChartDataIntervals.Month;
    }

    return chart.dateUnit;
  }
}
